import { Component, lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';

import ActivityIndicator from '~/components/ActivityIndicator';
import BootstrapDialog from '~/components/BootstrapDialog';

import { SCROLL_COMPONENT_ID } from '~/config';
import { chatRadarEnabledSelector } from '~/redux/selectors';
import { setMainPath } from '~/redux/slices/location';
import GDPRDialog from '~/routes/GDPRDialog';
import {
  DIAMONDS_PATH,
  diamondsDeepLinkEarnPath,
  diamondsDeepLinkSpendPath,
  HERO_PATH,
  isPopupPath,
  LEGAL_PATH,
  OPEN_CARDS_PATH,
  OPEN_PURCHASE_PATH,
  RADAR_PATH,
  USER_DETAILS_PATH,
} from '~/routes/paths';
import { getMainViewRoutes, routesInContainer } from '~/routes/routeComponents';
import SwitchWithLocationTracker from '~/routes/SwitchWithLocationTracker';

import EnsureLoggedInRoute from './EnsureLoggedInRoute';
import LootBoyContainer from './LootBoyContainer';
import { isMenuPopupPath } from './MenuRouter/menuRoutes';
import SoundContainer from './SoundContainer';

const LandingPageView = lazy(() => import('~/views/LandingPageView'));
const ComicDetailView = lazy(() => import('~/views/ComicDetailView'));
const OpenPurchaseView = lazy(() => import('~/views/OpenPurchaseView'));
const OpenCardsAnimation = lazy(() => import('~/views/OpenCardsAnimation'));
const SignumPaymentView = lazy(() => import('~/views/SignumPaymentView'));

const isModal = ({ pathname }) =>
  isMenuPopupPath(pathname) || isPopupPath(pathname);

const isModalWithDifferentLocation = (location, previousLocation) =>
  isModal(location) && previousLocation !== location;

class RootNavigator extends Component {
  static propTypes = {
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,

    language: PropTypes.string,
    dialog: PropTypes.object,
    mainViewRoutes: PropTypes.arrayOf(PropTypes.object),
    isLoggedIn: PropTypes.bool,
    captchaRequired: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      previousLocation: isModal(props.location)
        ? { pathname: '/' }
        : props.location,
    };

    this.unlistenHistory = this.props.history.listen(this.onHistoryChanged);
  }

  onHistoryChanged = (location, action) => {
    const { previousLocation } = this.state;

    if (location.state?.preventScrollToTop) {
      return;
    }

    if (
      action === 'PUSH' &&
      !isModalWithDifferentLocation(location, previousLocation)
    ) {
      window.scrollTo(0, 0);
      // eslint-disable-next-line no-unused-expressions
      document.getElementById(SCROLL_COMPONENT_ID)?.scrollTo(0, 0);
    }
  };

  componentDidUpdate(prevProps) {
    const { location, history, dialog } = this.props;
    if (prevProps.dialog !== dialog) {
      this.dialog.show(dialog);
    }

    if (location.state?.tutorialPath) {
      history.replace(location.pathname);
      history.push(location.state.tutorialPath);
    }
  }

  shouldComponentUpdate(nextProps) {
    const { location, history } = this.props;
    const { previousLocation } = this.state;

    if (
      location !== nextProps.location &&
      history.action !== 'POP' &&
      !isModalWithDifferentLocation(location, previousLocation)
    ) {
      this.setState({ previousLocation: location });
      return false;
    }
    return true;
  }

  componentWillUnmount() {
    this.unlistenHistory();
  }

  render() {
    const { location } = this.props;
    const { previousLocation } = this.state;
    let newLocation;

    if (isModalWithDifferentLocation(location, previousLocation)) {
      newLocation = previousLocation;
    } else if (!isModal(location)) {
      newLocation = location;
    }

    const shouldRenderFooter = !newLocation?.pathname?.startsWith?.(HERO_PATH);

    return (
      <>
        <SoundContainer pathname={location.pathname} />
        <BootstrapDialog
          ref={(a) => {
            this.dialog = a;
          }}
        />
        <GDPRDialog />
        <Suspense fallback={<ActivityIndicator />}>
          <SwitchWithLocationTracker
            location={newLocation}
            setPath={setMainPath}
          >
            <Redirect
              from="/recruiter"
              to={{
                pathname: USER_DETAILS_PATH,
                state: { openRecruiter: true },
              }}
            />
            <Redirect
              from="/rankingList"
              to="lootboysfinest"
            />
            <Redirect
              from="/contact"
              to={LEGAL_PATH}
            />
            <Redirect
              from="/home"
              to=""
            />
            <Redirect
              from="/ayeT"
              to="diamond-dungeon"
            />
            <Redirect
              from="/ayet-terms"
              to="diamond-dungeon-terms"
            />
            <Redirect
              from={diamondsDeepLinkEarnPath}
              to={DIAMONDS_PATH}
            />
            <Redirect
              from={diamondsDeepLinkSpendPath}
              to={DIAMONDS_PATH}
            />
            <Route
              component={ComicDetailView}
              path="/comics/:comicId"
            />
            <EnsureLoggedInRoute
              component={OpenPurchaseView}
              path={OPEN_PURCHASE_PATH}
            />
            <EnsureLoggedInRoute
              component={OpenCardsAnimation}
              path={OPEN_CARDS_PATH}
            />
            <Route
              path="/l/:_id/signum"
              component={SignumPaymentView}
            />
            <Route>
              <LootBoyContainer shouldRenderFooter={shouldRenderFooter}>
                <Suspense
                  fallback={
                    <div>
                      <ActivityIndicator />
                    </div>
                  }
                >
                  <Switch location={newLocation}>
                    {routesInContainer.map(
                      ({ ensureLoggedIn, ...route }, ndx) => {
                        const key = route.path.join?.() || route.path || ndx;
                        if (ensureLoggedIn) {
                          return (
                            <EnsureLoggedInRoute
                              key={key}
                              {...route}
                            />
                          );
                        }
                        return (
                          <Route
                            key={key}
                            {...route}
                          />
                        );
                      },
                    )}
                    {this.props.mainViewRoutes.map(
                      ({ panel: { component }, path, exact }) => (
                        <Route
                          key={path}
                          path={path}
                          component={component}
                          exact={exact}
                        />
                      ),
                    )}
                    <Route
                      component={LandingPageView}
                      path="/:pageIdOrKey"
                      exact
                    />
                  </Switch>
                </Suspense>
              </LootBoyContainer>
            </Route>
          </SwitchWithLocationTracker>
        </Suspense>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const user = state.auth.user.data;
  const isRadarEnabled = chatRadarEnabledSelector(state);

  const mainViewRoutes = getMainViewRoutes(isRadarEnabled ? [] : [RADAR_PATH]);

  return {
    language: state.auth.language,
    dialog: state.alerts.dialog,
    isLoggedIn: !!state.auth.token && !!user,
    mainViewRoutes,
  };
};

export default connect(mapStateToProps)(withRouter(RootNavigator));
